/**
 * 画面描画時間の状態機械（React 非依存・送り先非依存）。
 * T0 = 遷移開始（onBeforeLoad）、T1 = 最初の描画（onRendered 直後の rAF）、T2 = Template が主データを描画できた直後の rAF。
 * render_ms = T2 - T0（対応画面）/ T1 - T0（未対応画面）。確定結果は setScreenRenderSink で登録した送り先へ渡す。
 */
export type ScreenRenderStatus =
  | "ready"
  | "first_paint"
  | "timeout"
  | "abandoned"
  | "hidden";

export type ScreenRenderResult = {
  pathname: string;
  /** 起動時は true（T0 = navigationStart） */
  isInitial: boolean;
  status: ScreenRenderStatus;
  /** ready / first_paint のときだけ入る（ms 整数）。hidden・60 秒超は null */
  renderMs: number | null;
  firstPaintMs: number | null;
};

export type ScreenRenderBegin = {
  /** T0（performance.now() 基準・ms）。redirect 連鎖・連打では直前の値を引き継ぐ */
  startedAt: number;
  isInitial: boolean;
};

type Pending = {
  pathname: string;
  isInitial: boolean;
  /** useScreenReady を入れた画面か（SCREEN_READY_ROUTES）。false なら T1 で確定する */
  instrumented: boolean;
  startedAt: number;
  firstPaintAt: number | null;
  timer: ReturnType<typeof setTimeout> | null;
  hidden: boolean;
};

const READY_TIMEOUT_MS = 10_000;
const MAX_RENDER_MS = 60_000;

let pending: Pending | null = null;
let sink: ((result: ScreenRenderResult) => void) | null = null;

/** 確定時の送信先を登録する（main.tsx で 1 回） */
export const setScreenRenderSink = (
  next: ((result: ScreenRenderResult) => void) | null,
): void => {
  sink = next;
};

const isDocumentHidden = (): boolean =>
  typeof document !== "undefined" && document.visibilityState === "hidden";

const finalize = (
  p: Pending,
  status: ScreenRenderStatus,
  now: number,
): void => {
  // rAF / timer の発火時に別の pending へ入れ替わっていたら何もしない（連打で新画面を 0ms ready にしない）
  if (pending !== p) return;
  pending = null;
  if (p.timer) clearTimeout(p.timer);
  const firstPaintMs =
    p.firstPaintAt == null ? null : Math.round(p.firstPaintAt - p.startedAt);
  // ready は T2 - T0、first_paint は T1 - T0（打ち切り時刻ではない）
  const measured =
    status === "ready"
      ? Math.round(now - p.startedAt)
      : status === "first_paint"
        ? firstPaintMs
        : null;
  const usable = measured != null && !p.hidden && measured <= MAX_RENDER_MS;
  sink?.({
    pathname: p.pathname,
    isInitial: p.isInitial,
    status: p.hidden ? "hidden" : status,
    renderMs: usable ? measured : null,
    firstPaintMs,
  });
};

/**
 * 遷移開始（onBeforeLoad）。
 * 直前の pending が T1 にも達していなければ redirect 連鎖か連打なので、T0 を引き継いで画面だけ差し替える
 * （前の画面は確定させない）。T1 に達していれば abandoned で確定させる。
 */
export const beginScreenRender = (
  pathname: string,
  opts: { isInitial?: boolean; instrumented: boolean },
): ScreenRenderBegin => {
  const now = performance.now();
  let startedAt = opts.isInitial ? 0 : now;
  let isInitial = opts.isInitial ?? false;
  let hidden = isDocumentHidden();
  if (pending) {
    if (pending.firstPaintAt == null) {
      startedAt = pending.startedAt;
      isInitial = pending.isInitial;
      hidden = hidden || pending.hidden;
      if (pending.timer) clearTimeout(pending.timer);
      pending = null;
    } else {
      finalize(pending, "abandoned", now);
    }
  }
  const p: Pending = {
    pathname,
    isInitial,
    instrumented: opts.instrumented,
    startedAt,
    firstPaintAt: null,
    timer: null,
    hidden,
  };
  if (opts.instrumented) {
    p.timer = setTimeout(
      () =>
        finalize(
          p,
          p.firstPaintAt != null ? "first_paint" : "timeout",
          performance.now(),
        ),
      READY_TIMEOUT_MS,
    );
  }
  pending = p;
  return { startedAt, isInitial };
};

/** 最初の描画（onRendered から）。未対応画面はここで確定する */
export const markScreenFirstPaint = (): void => {
  const p = pending;
  if (!p || p.firstPaintAt != null) return;
  requestAnimationFrame(() => {
    if (pending !== p) return;
    p.firstPaintAt = performance.now();
    if (!p.instrumented) finalize(p, "first_paint", p.firstPaintAt);
  });
};

/** Template から（useScreenReady 経由）。pending の画面と一致するときだけ確定する */
export const markScreenReady = (pathname: string): void => {
  const p = pending;
  if (!p || p.pathname !== pathname) return;
  requestAnimationFrame(() => {
    const now = performance.now();
    // 主データが最初の描画に間に合っていた（T1 = T2）ケース
    if (p.firstPaintAt == null) p.firstPaintAt = now;
    finalize(p, "ready", now);
  });
};

/** 計測中に背面へ回った（描画が止まるので値を捨てる） */
export const markScreenHidden = (): void => {
  if (pending) pending.hidden = true;
};

/** テスト用 */
export const resetScreenRenderState = (): void => {
  if (pending?.timer) clearTimeout(pending.timer);
  pending = null;
};
